/**
 * v4.1 Phase 37.5 — /share/:token viewer page.
 *
 * Entry point for a share link. Validates the token via GET /api/shares/:token
 * and renders a read-only project summary with an explicit "viewing via
 * share link" banner. No edit affordances, no share/comment actions.
 *
 * Auth flow:
 *   - If unauthenticated: redirect to /login?redirect=/share/<token>&hint=share-link-signup
 *   - If authenticated + token valid + tenant matches: render
 *   - If authenticated + wrong tenant: show "link not accessible" message
 *
 * The frontend gating flag (COLLAB_MODE_ENABLED) hides the Share
 * button in ProjectHeader; the /share route itself stays live
 * regardless so existing share URLs keep working even after a flag
 * flip-off. Server enforces COLLAB_MODE too — the resolver returns
 * 404 when off, which the page handles gracefully.
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../components/Header.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";
import { resolveShareToken } from "../services/projects.js";
import { isAuthenticated } from "../services/auth.js";

export default function SharedProjectView() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;

    // Not signed in? Bounce to login with redirect-back context.
    if (!isAuthenticated()) {
      const redirect = encodeURIComponent(`/share/${token}`);
      window.location.assign(`/login?redirect=${redirect}&hint=share-link-signup`);
      return;
    }

    let cancelled = false;
    // setLoading(true) on mount is unnecessary — useState already
    // initialises it. For token-change we reset via key changes; lint
    // prefers setState calls happen inside async continuations.
    resolveShareToken(token)
      .then((out) => {
        if (cancelled) return;
        setData(out);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "This share link is invalid or expired");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="shared-project-viewer">
      <Header />
      <div className="shared-project-banner" role="status">
        You&apos;re viewing a shared project. Edits are disabled on share links.
      </div>
      <main className="shared-project-body">
        {loading ? (
          <>
            <LoadingSkeleton variant="line" />
            <LoadingSkeleton variant="line" />
            <LoadingSkeleton variant="line" size="short" />
          </>
        ) : error ? (
          <div>
            <h1>Share link unavailable</h1>
            <p>{error}</p>
            <Link to="/" className="btn btn-primary">
              Return to projects
            </Link>
          </div>
        ) : data ? (
          <div>
            <h1>{data.projectName}</h1>
            <p className="shared-project-meta">
              Shared by {data.ownerId}. Expires{" "}
              {new Date(data.expiresAt * 1000).toLocaleDateString()}.
            </p>
            <p>
              Project ID: <code>{data.projectId}</code>
            </p>
            <p>
              <em>
                Full read-only document viewing in the share context lands
                alongside Phase 38 notifications. For now this page
                confirms the link is valid and introduces the owner.
              </em>
            </p>
            <Link to={`/projects/${data.projectId}`} className="btn btn-primary">
              Open project
            </Link>
          </div>
        ) : null}
      </main>
    </div>
  );
}
